(() => {
  const experiments = window.VRExperiments;
  if (!experiments?.assignment || !experiments?.config) return;

  const { config, assignment } = experiments;
  const propertyValue = `${assignment.experimentId}:${assignment.variant}`;
  const eventBase = {
    experiment_id: assignment.experimentId,
    experiment_name: config.name,
    experiment_variant: assignment.variant,
    experiment_mode: config.mode,
    qa: assignment.qa,
    product_id: String(config.productId || ''),
    product_handle: config.productHandle || '',
    page_path: location.pathname
  };

  const publishShopifyEvent = (name, payload, attempt = 0) => {
    const shopifyPublish = window.Shopify?.analytics?.publish;
    if (typeof shopifyPublish === 'function') {
      try {
        const result = shopifyPublish(name, payload);
        if (result && typeof result.catch === 'function') {
          result.catch((error) => console.warn('[VitaRenew Experiments] Shopify event failed.', error));
        }
        return true;
      } catch (error) {
        console.warn('[VitaRenew Experiments] Shopify event failed.', error);
        return false;
      }
    }

    if (attempt < 20) {
      window.setTimeout(() => publishShopifyEvent(name, payload, attempt + 1), 250);
    }
    return false;
  };

  const publish = (eventName, detail = {}) => {
    if (!config.trackingEnabled || assignment.qa || experiments.active !== true) return false;
    const payload = { ...eventBase, ...detail };
    const published = publishShopifyEvent(`vitarenew:experiment_${eventName}`, payload);
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: `vitarenew_experiment_${eventName}`, ...payload });
    } catch (error) {}
    document.dispatchEvent(new CustomEvent(`vitarenew:experiment:${eventName}`, { detail: payload }));
    return published;
  };

  const cartProperties = () => experiments.cartProperties?.() || {};
  const mergeProperties = (properties = {}) => ({ ...properties, ...cartProperties() });

  const extrasTestTarget = () => {
    if (config.id !== 'pdp-extras-compact-2026-08') return null;
    const info = document.querySelector('.product__info-container');
    const description = info?.querySelector('.product__description');
    const buyForm = info?.querySelector('product-form[data-section-id]');
    if (!info || !description || !buyForm || (!config.includedConsole && !/deine\s+extras/i.test(description.textContent || ''))) return null;
    return { info, description, buyForm };
  };

  const applyExtrasVariant = () => {
    const target = extrasTestTarget();
    if (!target) return false;
    if (assignment.variant !== 'B') return true;

    const { info, description, buyForm } = target;
    const sourceText = description.textContent || '';
    const labels = config.includedLabels;
    if (!labels || info.querySelector('.vr-extras-compact')) return true;
    const extrasHeading = [...description.querySelectorAll('p')].find((p) => /(?:deine\s+extras|^\s*lieferumfang\s*:)/i.test(p.textContent));
    const extrasList = extrasHeading?.nextElementSibling;
    const extrasItems = extrasList?.matches('ol, ul') ? [...extrasList.children] : [];
    const cableItem = extrasItems.find((item) => /ladekabel/i.test(item.textContent));
    const storageItem = extrasItems.find((item) => /\d+\s*gb/i.test(item.textContent) && /sd/i.test(item.textContent));
    const hasReturns = /14\s*tage/i.test(sourceText);
    const modelIntro = description.querySelector('[data-vr-model-intro]');
    const cleanIncluded = config.includedConsole === true;
    extrasItems.filter((item) => /versand/i.test(item.textContent)).forEach((item) => item.remove());
    if (extrasItems.length) extrasHeading?.remove();

    const card = document.createElement('section');
    card.className = 'vr-extras-compact';
    if (cleanIncluded) {
      const layout = new URLSearchParams(location.search).get('included_layout') || 'pictures';
      card.dataset.includedLayout = ['tiles', 'pictures'].includes(layout) ? layout : 'compact';
      card.id = 'vr-included-preview';
    }
    card.setAttribute('aria-label', labels.heading);
    card.innerHTML = `
      <div class="vr-extras-compact__heading">
        <span class="vr-extras-compact__gift" aria-hidden="true">✓</span>
        <strong></strong>
      </div>
      <div class="vr-extras-compact__features"></div>
      <details class="vr-extras-compact__details">
        <summary>
          <svg class="vr-extras-compact__chevron" viewBox="0 0 24 24" width="20" height="20" fill="none" aria-hidden="true" focusable="false">
            <path d="m6 9 6 6 6-6" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </summary>
        <div class="vr-extras-compact__content"></div>
      </details>
    `;

    card.querySelector('strong').textContent = labels.heading;
    card.querySelector('summary').setAttribute('aria-label', labels.details);
    const featuresRoot = card.querySelector('.vr-extras-compact__features');
    card.querySelector('.vr-extras-compact__content').appendChild(description);
    if (cleanIncluded) {
      const styles = [...description.querySelectorAll('style')];
      if (modelIntro) description.replaceChildren(modelIntro, ...styles);
      else {
        [...description.querySelectorAll('p')].filter(p => /(?:deine\s+extras|^\s*lieferumfang\s*:)/i.test(p.textContent)).forEach(p => {
          if (p.nextElementSibling?.matches('ol, ul')) p.nextElementSibling.remove();
          p.remove();
        });
        extrasList?.remove();
        const paragraphs = new Set();
        [...description.querySelectorAll('p')].forEach(p => {
          const text = p.textContent.trim();
          if (paragraphs.has(text)) p.remove();
          else paragraphs.add(text);
        });
      }
      card.querySelector('details').open = card.dataset.includedLayout !== 'pictures';
      card.querySelector('summary').hidden = card.dataset.includedLayout !== 'pictures';
    }

    const buyBlock = buyForm.parentElement;
    buyBlock.insertAdjacentElement('afterend', card);
    const drawer = card.querySelector('details');
    const drawerHandle = drawer.querySelector('summary');
    const drawerContent = card.querySelector('.vr-extras-compact__content');
    const childrenRoot = document.createElement('div');
    if (card.dataset.includedLayout === 'pictures') {
      childrenRoot.className = 'vr-extras-compact__features vr-included-children';
      drawerContent.prepend(childrenRoot);
      featuresRoot.addEventListener('click', () => drawerHandle.click());
      featuresRoot.setAttribute('role', 'button');
      featuresRoot.tabIndex = 0;
      featuresRoot.setAttribute('aria-label', labels.details);
      featuresRoot.setAttribute('aria-expanded', 'false');
      featuresRoot.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); drawerHandle.click(); }
      });
    }
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
    let drawerAnimation = null;
    let drawerExpanded = drawer.open;
    drawer.dataset.expanded = String(drawerExpanded);
    drawerHandle.setAttribute('aria-expanded', String(drawerExpanded));
    drawerHandle.addEventListener('click', (event) => {
      event.preventDefault();
      const startHeight = drawer.open ? drawerContent.getBoundingClientRect().height : 0;
      if (drawerAnimation) {
        drawerAnimation.onfinish = null;
        drawerAnimation.cancel();
        drawerAnimation = null;
      }
      drawerExpanded = !drawerExpanded;
      drawer.dataset.expanded = String(drawerExpanded);
      drawerHandle.setAttribute('aria-expanded', String(drawerExpanded));
      if (card.dataset.includedLayout === 'pictures') featuresRoot.setAttribute('aria-expanded', String(drawerExpanded));
      if (reducedMotion.matches || typeof drawerContent.animate !== 'function') {
        drawer.open = drawerExpanded;
        drawerContent.style.overflow = '';
        return;
      }
      drawer.open = true;
      const endHeight = drawerExpanded ? drawerContent.getBoundingClientRect().height : 0;
      drawerContent.style.overflow = 'hidden';
      drawerAnimation = drawerContent.animate(
        [{ height: `${startHeight}px` }, { height: `${endHeight}px` }],
        { duration: 280, easing: 'cubic-bezier(.22, 1, .36, 1)' }
      );
      drawerAnimation.onfinish = () => {
        drawer.open = drawerExpanded;
        drawerContent.style.overflow = '';
        drawerAnimation = null;
      };
    });
    const savingsSummary = document.createElement('dl');
    savingsSummary.className = 'vr-included-savings';
    savingsSummary.setAttribute('aria-label', labels.savingsSummary || labels.heading);
    if (cleanIncluded) description.insertAdjacentElement('beforebegin', savingsSummary);
    const formatMoney = (cents) => new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(cents / 100);
    let previousSelection = '';
    const syncIncluded = () => {
      const addons = info.querySelector('[data-console-addons]');
      const nativeOption = (names) => {
        if (!cleanIncluded) return '';
        for (const name of names) {
          const field = info.querySelector(`variant-selects [data-option-name="${name}"]`);
          const value = field?.querySelector('input:checked, select')?.value;
          if (value) return value;
        }
        return '';
      };
      const storage = addons?.querySelector('input[name$="_storage"]:checked')?.value || nativeOption(['speicher', 'storage']) || config.baseStorage || '32GB';
      const hardware = addons?.querySelector('input[name$="_hardware"]:checked')?.value || nativeOption(['ladeport', 'ladeanschluss', 'hardware-modifikation', 'mod']) || 'Original';
      const battery = addons?.querySelector('input[name$="_battery"]:checked')?.value || 'Standard';
      const variantId = buyForm.querySelector('[name="id"]')?.value;
      const selectionKey = `${storage}|${hardware}|${battery}|${variantId}`;
      if (selectionKey === previousSelection) return;
      previousSelection = selectionKey;
      const size = storage.replace(/\s*GB/i, ' GB');
      const storageLabel = labels.storage.replace('__SIZE__', size);
      const cables = config.vitaTv ? [] : /capture/i.test(hardware) ? [labels.cable3ds, labels.cableUsb]
        : /usb[\s-]*c/i.test(hardware) ? [labels.cableUsb] : [labels.cable3ds];
      let addonConfig = {};
      try {
        addonConfig = JSON.parse(addons?.querySelector('[data-console-addon-config]')?.textContent || '{}');
        if (typeof addonConfig === 'string') addonConfig = JSON.parse(addonConfig);
      } catch (_) {}
      const variantAddons = addonConfig.variants?.[variantId] || {};
      const storagePrice = variantAddons.storage?.[storage]?.price;
      const hardwarePrice = variantAddons.hardware?.[hardware]?.price;
      const batteryPrice = Number(addons?.dataset.xlBatteryPrice);
      const hardwareLabel = hardwarePrice > 0 ? (/capture/i.test(hardware) ? '1 × Capture Card' : /usb[\s-]*c/i.test(hardware) ? '1 × USB-C Port' : null) : null;
      const consoleLabel = `1 × ${config.productTitle}`;
      const stylusLabel = `1 × ${labels.stylus}`;
      const accessoryHandles = new Map([
        [`1 × ${labels.cable3ds}`, config.standardCableHandle || '3ds-ladekabel'],
        [`1 × ${labels.cableUsb}`, 'usb-c-ladekabel'],
        [stylusLabel, '3-ersatz-stifte-fur-den-3ds-xl'],
        [`1 × ${config.includedAccessories?.['ps3-controller']?.label}`, 'ps3-controller'],
        [`1 × ${storageLabel}`, storage === (config.baseStorage || '32GB') || config.nativeIncludedStorage ? (storage === '128GB' ? '128gb-speicher-upgrade-fur-nintendo-3ds' : storage === '32GB' ? '32gb-speicher' : 'storage-'+storage.toLowerCase()) : null]
      ]);
      const costs = new Map([
        [`1 × ${storageLabel}`, storage === (config.baseStorage || '32GB') || config.nativeIncludedStorage ? 0 : storagePrice],
        ['1 × XL Akku', batteryPrice],
        [hardwareLabel, hardwarePrice]
      ]);
      const features = [
        ...(card.dataset.includedLayout === 'pictures' ? [consoleLabel] : []),
        ...(cleanIncluded || cableItem ? cables.map((cable) => cleanIncluded ? `1 × ${cable}` : cable) : []),
        ...(cleanIncluded || storageItem ? [cleanIncluded ? `1 × ${storageLabel}` : storageLabel] : []),
        ...(cleanIncluded ? (config.includedAccessories?.['3-ersatz-stifte-fur-den-3ds-xl']?.variantId > 0 ? [stylusLabel] : []) : hasReturns ? [labels.returns] : []),
        ...(config.vitaTv ? [`1 × ${config.includedAccessories?.['ps3-controller']?.label}`, `1 × ${labels.powerCable}`] : []),
        ...(cleanIncluded && /xl/i.test(battery) ? ['1 × XL Akku'] : []),
        ...(cleanIncluded && hardwareLabel ? [hardwareLabel] : [])
      ];
      if (cleanIncluded) features.sort((a, b) => Number((costs.get(a) || 0) > 0) - Number((costs.get(b) || 0) > 0));
      if (cleanIncluded) {
        const savings = features.reduce((total, label) => {
          const accessory = config.includedAccessories?.[accessoryHandles.get(label)];
          const amount = costs.has(label) ? costs.get(label) : 0;
          return total + (accessory?.price > 0 && Number(amount) === 0 ? accessory.price : 0);
        }, 0) + (window.VR3DSPromo?.active() ? Number(document.getElementById('vr-promo-included')?.dataset.retail || 0) : 0);
        savingsSummary.hidden = savings <= 0;
        const valueRow = document.createElement('div');
        const valueLabel = document.createElement('dt');
        valueLabel.textContent = labels.accessoryValue;
        const value = document.createElement('dd');
        const retail = document.createElement('s');
        retail.textContent = formatMoney(savings);
        value.append(retail, document.createTextNode(` ${formatMoney(0)}`));
        valueRow.append(valueLabel, value);
        const savedRow = document.createElement('div');
        savedRow.className = 'vr-included-savings__saved';
        const savedLabel = document.createElement('dt');
        savedLabel.textContent = labels.youSave;
        const saved = document.createElement('dd');
        saved.textContent = formatMoney(savings);
        savedRow.append(savedLabel, saved);
        savingsSummary.replaceChildren(valueRow, savedRow);
      }
      const featureNodes = features.map((label) => {
        const item = document.createElement('span');
        item.textContent = label;
        if (card.dataset.includedLayout === 'pictures') {
          const cdn = 'https://cdn.shopify.com/s/files/1/0929/9456/8533/files/';
          let src = '';
          const accessory = config.includedAccessories?.[accessoryHandles.get(label)];
          if (accessory) src = accessory.image || '';
          else if (label === `1 × ${storageLabel}`) src = config.includedAccessories?.['storage-'+storage.toLowerCase()]?.image || config.includedAccessories?.['128gb-speicher-upgrade-fur-nintendo-3ds']?.image || '';
          else if (label === consoleLabel) src = document.querySelector('[id^="MediaGallery-"] img')?.src || '';
          else if (label.includes('Stylus')) src = cdn + 'Stylus_Nintendo_3ds_xl_3DSRenew.webp?width=160';
          else if (label.includes('3DS-Ladekabel')) src = cdn + 'nintendo_3ds_xl_Ladekabel.webp?width=160';
          else if (label.includes('128')) src = cdn + '128gb-speicher.png?width=160';
          else if (label.includes('32 GB')) src = modelIntro?.dataset.sd32Image || '';
          else if (label.includes('XL Akku')) src = cdn + 'xl-akku.png?width=160';
          else if (label.includes('USB-C-Ladekabel')) src = modelIntro?.dataset.usbcImage || '';
          else if (label.includes('Capture Card')) src = cdn + 'capture-card_3a78848f-d145-4f05-aa71-121e1c2315f5.png?width=160';
          else if (label.includes('USB-C Port')) src = cdn + 'usb-c-upgrade_aac08634-1df4-414a-a6ee-5f2b92a58a3b.png?width=160';
          else {
            const drawing = label.includes('SD')
              ? '<path d="M35 20h42v65H23V34z" fill="#272b32"/><path d="M38 22v12m10-12v12m10-12v12m10-12v12" stroke="#d7b66c" stroke-width="5"/><text x="50" y="65" text-anchor="middle" fill="white" font-size="17" font-family="Arial">32 GB</text>'
              : '<path d="M32 35v28q0 17 18 17t18-17V35" fill="none" stroke="#34383e" stroke-width="5"/><rect x="23" y="18" width="18" height="23" rx="4" fill="#34383e"/><rect x="59" y="18" width="18" height="23" rx="8" fill="#34383e"/><path d="M27 21h10m26 0h10" stroke="#b4bcc6" stroke-width="4"/>';
            src = 'data:image/svg+xml,' + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">' + drawing + '</svg>');
          }
          const img = document.createElement('img');
          img.src = src;
          img.alt = '';
          img.width = 80;
          img.height = 80;
          img.loading = 'lazy';
          item.prepend(img);
          item.classList.add(label === consoleLabel ? 'vr-included-console' : 'vr-included-child');
          const name = document.createElement('b');
          name.className = 'vr-included-name';
          name.textContent = label;
          item.replaceChildren(img, name);
          if (label !== consoleLabel) {
            const price = document.createElement('small');
            price.className = 'vr-included-price';
            const amount = costs.has(label) ? costs.get(label) : 0;
            price.textContent = Number.isFinite(Number(amount)) ? new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(Number(amount) / 100) : '–';
            if (accessory?.price > 0 && Number(amount) === 0) {
              const retail = document.createElement('s');
              retail.textContent = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(accessory.price / 100);
              price.prepend(retail, document.createTextNode(' '));
            }
            item.appendChild(price);
          }
        }
        return item;
      });
      if (card.dataset.includedLayout === 'pictures') {
        featuresRoot.replaceChildren(featureNodes[0]);
        childrenRoot.replaceChildren(...featureNodes.slice(1));
        const promoTemplate = document.getElementById('vr-promo-included');
        if (promoTemplate && window.VR3DSPromo?.active()) childrenRoot.append(promoTemplate.content.cloneNode(true));
      } else featuresRoot.replaceChildren(...featureNodes);
      if (cableItem) cableItem.textContent = cables.map((cable) => `1 × ${cable}`).join(' + ');
      if (storageItem) storageItem.textContent = `1 × ${storageLabel}`;
    };
    document.addEventListener('vr-promo:ready', () => { previousSelection = ''; syncIncluded(); });
    syncIncluded();
    info.addEventListener('console-addons:change', syncIncluded);
    info.addEventListener('change', syncIncluded);
    new MutationObserver((mutations) => {
      if (mutations.some((mutation) => !card.contains(mutation.target))) syncIncluded();
    }).observe(info, { childList: true, subtree: true });
    return true;
  };

  const syncForm = (form) => {
    if (!(form instanceof HTMLFormElement) || assignment.qa) return;
    let input = form.querySelector('input[data-vr-experiment-property]');
    if (!input) {
      input = document.createElement('input');
      input.type = 'hidden';
      input.name = 'properties[_vr_experiment]';
      input.dataset.vrExperimentProperty = 'true';
      form.appendChild(input);
    }
    input.value = propertyValue;
  };

  const syncForms = (scope = document) => {
    scope.querySelectorAll('form[action*="/cart/add"]').forEach(syncForm);
  };

  const syncCartAttribute = async () => {
    if (assignment.qa) return;
    const sessionKey = `vr_cart_assignment_synced:${propertyValue}`;
    try {
      if (sessionStorage.getItem(sessionKey) === '1') return;
    } catch (error) {}
    try {
      const response = await fetch(window.routes?.cart_update_url || '/cart/update.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ attributes: { vr_experiment__: propertyValue } })
      });
      if (!response.ok) throw new Error(`Cart attribute failed (${response.status}).`);
      try { sessionStorage.setItem(sessionKey, '1'); } catch (error) {}
    } catch (error) {
      console.warn('[VitaRenew Experiments] Cart assignment could not be synchronized.', error);
    }
  };

  experiments.publish = publish;
  experiments.track = publish;
  experiments.mergeProperties = mergeProperties;

  const init = () => {
    if (!applyExtrasVariant()) return;
    if (config.mode === 'standard') return;
    experiments.active = true;
    syncForms();
    new MutationObserver((mutations) => {
      mutations.forEach((mutation) => mutation.addedNodes.forEach((node) => {
        if (!(node instanceof Element)) return;
        if (node.matches?.('form[action*="/cart/add"]')) syncForm(node);
        syncForms(node);
      }));
    }).observe(document.body, { childList: true, subtree: true });

    const exposureKey = `vr_exposure:${propertyValue}`;
    let alreadySent = false;
    try { alreadySent = sessionStorage.getItem(exposureKey) === '1'; } catch (error) {}
    if (!alreadySent) {
      window.setTimeout(() => {
        publish('exposure');
        try { sessionStorage.setItem(exposureKey, '1'); } catch (error) {}
      }, 750);
    }
    syncCartAttribute();
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
})();
